import { Dialog, TextInput } from '@ordr-as/umami-react'
import { useState, useEffect } from 'react'
import { useVerifyGlobalFeePin } from 'hooks/usePaymentFeeApi'

type PinCodeDialogProps = {
    isOpen: boolean
    onSuccess: () => void
    onCancel: () => void
    title?: string
    message?: string
}

const PinCodeDialog = (props: PinCodeDialogProps) => {
    const { isOpen, onSuccess, onCancel, title = 'PIN Required', message = 'Enter PIN to proceed' } = props

    const [pinInput, setPinInput] = useState('')
    const [error, setError] = useState('')

    const { mutate: verifyPin, isLoading: isVerifying } = useVerifyGlobalFeePin()

    // Reset state when dialog opens/closes
    useEffect(() => {
        if (isOpen) {
            setPinInput('')
            setError('')
        }
    }, [isOpen])

    const handleVerify = () => {
        verifyPin(pinInput, {
            onSuccess: (response) => {
                if (response.valid) {
                    onSuccess()
                    setPinInput('')
                    setError('')
                } else {
                    setError('Incorrect PIN. Please try again.')
                    setPinInput('')
                }
            },
            onError: () => {
                setError('Failed to verify PIN. Please try again.')
                setPinInput('')
            },
        })
    }

    const handleCancel = () => {
        setPinInput('')
        setError('')
        onCancel()
    }

    return (
        <>
            {isOpen && (
                <Dialog
                    title={title}
                    onClose={handleCancel}
                    actionLabel={isVerifying ? 'Verifying...' : 'Verify'}
                    onAction={handleVerify}
                    secondaryActionLabel="Cancel"
                    onSecondaryAction={handleCancel}
                    isDisabledAction={pinInput.length === 0 || isVerifying}
                    isDisabledSecondaryAction={isVerifying}
                >
                    <div className="flex flex-col gap-4">
                        <div className="bg-yellow-lightest border border-yellow-light rounded p-3 text-small">
                            {message}
                        </div>

                        <div>
                            <label className="block text-small font-medium text-grey-darkest mb-2">PIN Code</label>
                            <TextInput
                                value={pinInput}
                                onChange={(value: string) => {
                                    setPinInput(value)
                                    setError('') // Clear error on new input
                                }}
                                placeholder="Enter PIN"
                                fullWidth
                                type="password"
                            />
                            {error && <p className="text-small text-red mt-2">{error}</p>}
                        </div>

                        <div className="text-small text-grey-dark">
                            Global fees impact company earnings. Please verify your authorization to proceed.
                        </div>
                    </div>
                </Dialog>
            )}
        </>
    )
}

export default PinCodeDialog
